import classNames from 'classnames'
import styles from './Button.module.css'
import { type AllowedButtonTags, type ButtonKind, type ButtonProps, type ButtonSize, type ButtonType } from './types'
import { forwardRef, useId } from 'react'

function getButtonSizeStyle(size: ButtonSize) {
  return styles[`size-${size}`]
}

// eslint-disable-next-line import/exports-last
export function getButtonKindStyles(kind: ButtonKind, ghost?: boolean) {
  return [
    {
      [styles['ghost']!]: ghost,
    },
    styles[`kind-${kind}`]!,
  ]
}

// eslint-disable-next-line import/exports-last
export function headlessButtonProps<T extends AllowedButtonTags>({
  children,
  kind = 'primary',
  size = 'default',
  'as': Tag = 'button' as T,
  'className': userClassName,
  ghost,
  busy,
  id,
  'aria-disabled': ariaDisabled_,
  'aria-describedby': ariaDescribedBy_,
  disabledReason,
  disabled_,
  disabled,
  fullWidth,
  ...props
}: ButtonProps<T>) {
  const buttonDisabled = disabled || disabled_
  const ariaDisabled = buttonDisabled || ariaDisabled_ || busy
  const disabledReasonId = ariaDisabled && disabledReason ? `${id}-disabled-reason` : undefined
  const ariaDescribedBy = ariaDescribedBy_ ?? disabledReasonId

  const className = classNames(
    styles.button,
    getButtonSizeStyle(size),
    userClassName,
    ...getButtonKindStyles(kind, ghost),
    {
      [styles.busy!]: busy,
      [styles.idle!]: !busy,
      ['fullWidth']: fullWidth && fullWidth !== 'mobile-only',
      ['fullWidthOnMobile']: fullWidth && fullWidth === 'mobile-only',
    }
  )

  const children_ = (
    <>
      {disabledReasonId && (
        <span id={disabledReasonId} className={styles.srOnly}>
          {disabledReason}
        </span>
      )}
      {children}
    </>
  )

  return {
    className,
    'children': children_,
    'aria-disabled': ariaDisabled,
    'aria-describedby': ariaDescribedBy,
    'aria-busy': busy,
    'disabled': disabled_,
    id,
    ...props,
  }
}

const Button = forwardRef(function Button<T extends AllowedButtonTags = 'button'>(
  { id: id_, ...props }: ButtonProps<T>,
  ref: React.Ref<HTMLElement>
) {
  const generatedId = useId()
  const id = id_ || generatedId
  const { children, ...props_ } = headlessButtonProps({ ...props, id } as ButtonProps<T>)
  if (props.as === 'a') {
    return (
      <a ref={ref as React.Ref<HTMLAnchorElement>} {...props_}>
        {children}
      </a>
    )
  }

  return (
    <button ref={ref as React.Ref<HTMLButtonElement>} {...props_}>
      {children}
    </button>
  )
}) as ButtonType

export default Button
